import React, { useEffect, useRef, useState } from "react";
import { Amplify } from "aws-amplify";
import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  signIn,
  signInWithRedirect,
  signUp,
} from "aws-amplify/auth";
import { USER_AUTH_CONFIG } from "../config/aws-config";
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  VpnKey as VpnKeyIcon,
} from "@mui/icons-material";
import {
  checkEmailExists,
  saveAuthRelay,
  checkCallbackAuthRelay,
  checkPairingAuthRelay,
} from "./user-login-service";
import VerifyCode from "./verify-code";
import UserSignInChallenge, {
  signInChallengeMessage,
} from "./user-sign-in-challenge";
import UserLoginNotice, {
  loginNotice,
  loginRequesterName,
} from "./user-login-notice";
import { userLoginError } from "./user-login-errors";
import {
  clearUserBrowserSession,
  getUserRelayAuthState,
} from "./user-auth-session";
import { USER_AUTH_BACKGROUND, UserAuthCard } from "./user-auth-layout";

const USER_CALLBACK_INTENT_KEY = "mhome.pampas.user.callback-relay.v1";
const USER_LOGIN_CONTEXT_KEY = "mhome.pampas.user.login-context.v1";
const USER_CALLBACK_INTENT_TTL_MILLIS = 5 * 60 * 1000;

function saveLoginContext(update) {
  let current = {};
  try {
    current = JSON.parse(
      sessionStorage.getItem(USER_LOGIN_CONTEXT_KEY) || "{}"
    );
  } catch (_) {
    current = {};
  }
  sessionStorage.setItem(
    USER_LOGIN_CONTEXT_KEY,
    JSON.stringify({ ...current, ...update })
  );
}

// Function to generate a random state parameter for OAuth
function generateRandomState() {
  const array = new Uint32Array(8);
  window.crypto.getRandomValues(array);
  return Array.from(array, (dec) => ("0" + dec.toString(16)).substr(-2)).join(
    ""
  );
}

const UserLogin = () => {
  const [activeTab, setActiveTab] = useState(0);
  const authInFlight = useRef(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [deliveryPending, setDeliveryPending] = useState(false);
  const showCredentials = !challenge && !deliveryPending;

  const runAuth = async (operation, stage) => {
    if (authInFlight.current) return;
    authInFlight.current = true;
    setAuthBusy(true);
    setAuthStage(stage);
    setErrorMessage(null);
    setInfoMessage(null);
    try {
      await operation();
    } catch (error) {
      setErrorMessage(userLoginError(error, stage));
    } finally {
      authInFlight.current = false;
      setAuthBusy(false);
    }
  };
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [notice, setNotice] = useState(null);
  const setErrorMessage = (message) =>
    setNotice((current) =>
      message
        ? { kind: "error", message }
        : current?.kind === "error"
          ? null
          : current
    );
  const setInfoMessage = (message) =>
    setNotice((current) =>
      message
        ? { kind: "success", message }
        : current?.kind === "success"
          ? null
          : current
    );
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showResetFields, setShowResetFields] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loadingForgotPassword, setLoadingForgotPassword] = useState(false);
  const theme = useTheme();
  const [countdown, setCountdown] = useState(0);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const flow = queryParams.get("flow");
  const code = queryParams.get("id");
  const [callbackTicket, setCallbackTicket] = useState("");
  const [clientDisplayName, setClientDisplayName] = useState("Your app");
  const [authStage, setAuthStage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Pairing states
  const [pairingCode, setPairingCode] = useState("");
  const [isPairing, setIsPairing] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [isValidRequest, setIsValidRequest] = useState(true);

  const showNormalLoginForm =
    isValidRequest && showLoginForm && showCredentials;

  // Session expiration states
  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [timeRemaining, setTimeRemaining] = useState(5 * 60); // 5 minutes in seconds
  const [showExpirationWarning, setShowExpirationWarning] = useState(false);

  // Terms and conditions checkbox
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [validations, setValidations] = useState({
    hasLowerCase: false,
    hasUpperCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasMinLength: false,
    noLeadingTrailingSpace: false,
  });

  useEffect(() => {
    Amplify.configure(USER_AUTH_CONFIG);
    saveLoginContext({ flow, relayId: code });

    let ticket = "";
    if (flow === "callback") {
      const fragment = new URLSearchParams(window.location.hash.slice(1));
      ticket = fragment.get("ticket") || "";
      if (ticket && code) {
        sessionStorage.setItem(
          USER_CALLBACK_INTENT_KEY,
          JSON.stringify({
            relayId: code,
            loginTicket: ticket,
            createdAt: Date.now(),
          })
        );
        window.history.replaceState(
          null,
          "",
          `${window.location.pathname}${window.location.search}`
        );
      } else {
        try {
          const stored = JSON.parse(
            sessionStorage.getItem(USER_CALLBACK_INTENT_KEY) || "{}"
          );
          if (
            stored.relayId === code &&
            Number(stored.createdAt) + USER_CALLBACK_INTENT_TTL_MILLIS >
              Date.now()
          ) {
            ticket = stored.loginTicket || "";
          } else {
            sessionStorage.removeItem(USER_CALLBACK_INTENT_KEY);
          }
        } catch (_) {
          ticket = "";
        }
      }
      setCallbackTicket(ticket);
    }

    // Validate flow and relay parameters.
    validateRequest(ticket);

    if (flow === "callback" && code && ticket) {
      checkCallbackAuthRelay(code, ticket)
        .then((result) => {
          setClientDisplayName(
            loginRequesterName(result.clientId, result.clientDisplayName)
          );
          setIsValidRequest(true);
          setShowLoginForm(true);
        })
        .catch((error) => {
          setIsValidRequest(false);
          setShowLoginForm(false);
          setErrorMessage(error.message);
        });
    }

    // Start session timer
    setSessionStartTime(Date.now());
    setTimeRemaining(5 * 60);
    setShowExpirationWarning(false);
  }, [flow, code]);

  // Session expiration timer
  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - sessionStartTime) / 1000);
      const remaining = 5 * 60 - elapsed; // 5 minutes - elapsed time

      if (remaining <= 0) {
        clearInterval(interval);
        setTimeRemaining(0);
        setIsValidRequest(false);
        setShowLoginForm(false);
        return;
      }

      if (remaining <= 60 && !showExpirationWarning) {
        // Show warning when less than 1 minute left
        setShowExpirationWarning(true);
      }

      setTimeRemaining(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [sessionStartTime, showExpirationWarning, flow]);

  const validateRequest = (resolvedCallbackTicket = callbackTicket) => {
    if (!flow) {
      setIsValidRequest(false);
      setErrorMessage(
        "This sign-in request is invalid. Please return to your app and start again."
      );
      return;
    }

    if (flow === "pairing") {
      if (!code) {
        setIsValidRequest(false);
        setErrorMessage(
          "This sign-in request is invalid. Please return to your app and start again."
        );
        return;
      }
      setIsValidRequest(true);
      setShowLoginForm(false);
    } else if (flow === "callback") {
      if (!code || !resolvedCallbackTicket) {
        setIsValidRequest(false);
        setErrorMessage(
          "This sign-in request is invalid. Please return to your app and start again."
        );
        return;
      }
      setIsValidRequest(true);
      setShowLoginForm(false);
    } else {
      // Invalid flow
      setIsValidRequest(false);
      setErrorMessage(
        "This sign-in request is invalid. Please return to your app and start again."
      );
      return;
    }

    // Clear any previous error messages if validation passes
    setErrorMessage("");
  };

  const deliverAuthenticatedSession = async (authState) => {
    if (flow === "callback") {
      const resp = await saveAuthRelay({
        ...authState,
        flow: "callback",
        relayId: code,
        loginTicket: callbackTicket,
      });
      if (!resp.redirectUri) {
        throw new Error("Missing login callback.");
      }
      sessionStorage.removeItem(USER_CALLBACK_INTENT_KEY);
      try {
        await clearUserBrowserSession();
      } finally {
        window.location.replace(resp.redirectUri);
      }
      return;
    }

    await saveAuthRelay({
      ...authState,
      flow: "pairing",
      relayId: code,
      pairingCode,
    });
    window.location.href = "/auth-granted";
  };

  const validatePassword = (pwd) => {
    setValidations({
      hasLowerCase: /[a-z]/.test(pwd),
      hasUpperCase: /[A-Z]/.test(pwd),
      hasNumber: /\d/.test(pwd),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<> ]/.test(pwd),
      hasMinLength: pwd.length >= 8,
      noLeadingTrailingSpace: pwd.trim().length === pwd.length,
    });
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    validatePassword(value);
  };

  const allValid = Object.values(validations).every(Boolean);

  const startCountdown = () => {
    setCountdown(60);
    setIsCountdownActive(true);

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) {
          clearInterval(timer);
          setIsCountdownActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const finishSignIn = async () => {
    setChallenge(null);
    setPassword("");
    setDeliveryPending(true);
    let stage = "session";
    try {
      const authState = await getUserRelayAuthState();
      stage = "delivery";
      await deliverAuthenticatedSession(authState);
    } catch (error) {
      setErrorMessage(userLoginError(error, stage));
    }
  };

  const processSignInResult = async (result) => {
    if (result?.isSignedIn || result?.nextStep?.signInStep === "DONE") {
      localStorage.removeItem("filterParams-taskflow");
      localStorage.removeItem("filterParams-recurring");
      await finishSignIn();
      return;
    }
    const step = result?.nextStep;
    if (step?.signInStep === "RESET_PASSWORD") {
      setChallenge(null);
      await handleForgotPassword();
      return;
    }
    const supported = [
      "CONFIRM_SIGN_IN_WITH_SMS_CODE",
      "CONFIRM_SIGN_IN_WITH_EMAIL_CODE",
      "CONFIRM_SIGN_IN_WITH_TOTP_CODE",
      "CONTINUE_SIGN_IN_WITH_TOTP_SETUP",
      "CONTINUE_SIGN_IN_WITH_EMAIL_SETUP",
      "CONTINUE_SIGN_IN_WITH_MFA_SELECTION",
      "CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION",
      "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED",
      "CONFIRM_SIGN_UP",
    ];
    if (supported.includes(step?.signInStep)) {
      setPassword("");
      setChallenge(step);
    } else {
      setErrorMessage(
        "This account requires a verification method that is not supported on this page. Please contact support."
      );
    }
  };

  const backToSignIn = () => {
    if (authInFlight.current) return;
    setChallenge(null);
    setDeliveryPending(false);
    setPassword("");
    setErrorMessage(null);
  };

  const handleChallenge = (response, userAttributes) =>
    runAuth(async () => {
      if (challenge.signInStep === "CONFIRM_SIGN_UP") {
        await confirmSignUp({
          username: email.trim(),
          confirmationCode: response.trim(),
        });
        setChallenge(null);
        setInfoMessage("Your email is confirmed. Please sign in.");
        return;
      }
      await processSignInResult(
        await confirmSignIn({
          challengeResponse:
            challenge.signInStep ===
            "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
              ? response
              : response.trim(),
          ...(Object.keys(userAttributes).length
            ? { options: { userAttributes } }
            : {}),
        })
      );
    }, "verification");

  const handleSignIn = async () => {
    if (!termsAccepted) {
      setErrorMessage(
        "Please accept the Terms of Use and Privacy Policy to continue."
      );
      return;
    }
    setEmailError(!email.trim());
    setPasswordError(!password);
    if (!email.trim() || !password) return;
    await runAuth(async () => {
      // A new credential attempt must not inherit an earlier account. Only
      // clear browser tokens: signOut can revoke a session already handed off.
      await clearUserBrowserSession();
      await processSignInResult(
        await signIn({
          username: email.trim(),
          password,
          options: { authFlowType: "USER_SRP_AUTH" },
        })
      );
    }, "password");
  };

  const handleForgotPassword = async () => {
    setErrorMessage(null);
    setInfoMessage(null);
    setLoadingForgotPassword(true);
    try {
      if (!email) {
        setErrorMessage("Please enter the email.");
        return;
      }
      await resetPassword({ username: email.trim() });
      setShowResetFields(true);
      setNewPassword("");
      setResetCode("");
      setInfoMessage("Password reset code sent to your email.");
    } catch (error) {
      setErrorMessage("Failed to send reset code. Please try again.");
    } finally {
      setLoadingForgotPassword(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      await confirmResetPassword({
        username: email.trim(),
        confirmationCode: resetCode,
        newPassword,
      });
      setInfoMessage("Password reset successful! You can now sign in.");
      setShowResetFields(false);
      setActiveTab(0); // Switch to SignIn tab after reset
    } catch (error) {
      setErrorMessage("Failed to reset password. Please check your code.");
    }
  };

  const handleGoogleSignIn = async () => {
    // Check if terms are accepted
    if (!termsAccepted) {
      setErrorMessage(
        "Please accept the Terms of Use and Privacy Policy to continue."
      );
      return;
    }

    await runAuth(async () => {
      await clearUserBrowserSession();
      await signInWithRedirect({ provider: "Google" });
    }, "google");
  };

  const handleSignUp = async () => {
    setErrorMessage("");
    setInfoMessage("");

    // Check if terms are accepted
    if (!termsAccepted) {
      setErrorMessage(
        "Please accept the Terms of Use and Privacy Policy to continue."
      );
      return;
    }

    try {
      const valid = await checkEmailPassword();
      if (!valid) {
        return;
      }

      await signUp({
        username: email,
        password,
        options: { userAttributes: { email } },
      });
      setInfoMessage(
        "Verification code sent to your email.Valid within five minutes"
      );
      setShowConfirm(true);
      setCodeSent(true);
      startCountdown();
    } catch (error) {
      console.error("Error during sign up:", error);
      setErrorMessage("Error signing up. Please try again.");
    }
  };

  const checkEmailPassword = async () => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validEmail = emailPattern.test(email);

    if (!email || !validEmail) {
      setErrorMessage("Invalid email");
      setEmailError(true);
      return false;
    } else {
      setEmailError(false);
    }

    const criteria = {
      lowerCase: /[a-z]/, // At least one lowercase letter
      upperCase: /[A-Z]/, // At least one uppercase letter
      number: /[0-9]/, // At least one number
      specialCharacter: /[!@#$%^&*(),.?":{}|<>]/, // At least one special character
      length: /^.{8,}$/, // At least 8 characters
      noLeadingTrailingSpace: /^\S.*\S$|^\S$/, // No leading or trailing spaces
    };

    const validPwd =
      criteria.lowerCase.test(password) &&
      criteria.upperCase.test(password) &&
      criteria.number.test(password) &&
      criteria.specialCharacter.test(password) &&
      criteria.length.test(password) &&
      criteria.noLeadingTrailingSpace.test(password);

    if (!password || !validPwd) {
      setErrorMessage("Invalid password");
      setPasswordError(true);
      return false;
    } else {
      setPasswordError(false);
    }

    const emailExists = await checkEmailExists(email);

    if (emailExists) {
      setErrorMessage("This email is already registered. Please log in.");
      return false;
    }

    return true;
  };

  const handleResendCode = async () => {
    const valid = await checkEmailPassword();
    if (!valid) {
      return;
    }

    try {
      await resendSignUpCode({ username: email });
      setInfoMessage(
        "New verification code sent to your email. Valid within five minutes"
      );
      setShowConfirm(true);
      startCountdown();
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message);
    }
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    validatePassword(value);
  };

  const handleConfirmSignUp = async () => {
    try {
      await confirmSignUp({
        username: email,
        confirmationCode: verificationCode,
      });
      setInfoMessage("Sign up successful! You can now sign in.");
      setShowConfirm(false);
      handleTabSwitch(0);
    } catch (error) {
      setErrorMessage("Invalid verification code. Please try again.");
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleTabSwitch = (tab) => {
    if (authInFlight.current) return;
    setErrorMessage("");
    setInfoMessage("");
    setActiveTab(tab);
  };

  const handlePairing = async (value) => {
    if (!code || !value || value.length !== 6) {
      setErrorMessage("Invalid pairing code");
      return;
    }

    setIsPairing(true);
    setErrorMessage("");
    setInfoMessage("");

    try {
      const response = await checkPairingAuthRelay(code, value);
      if (response.success) {
        saveLoginContext({ pairingCode: value });
        setShowLoginForm(true);
      } else {
        setErrorMessage("Invalid pairing code.");
      }
    } catch (error) {
      console.error("Pairing error:", error);
      setErrorMessage("Invalid pairing code.");
    } finally {
      setIsPairing(false);
    }
  };

  const handlePairingCodeChange = (value) => {
    setPairingCode(value);
  };

  const handlePairingCodeComplete = (value) => {
    handlePairing(value);
  };

  // Chrome autofill only paints the <input>; extend tint to full field (incl. adornments).
  const autofillBg =
    theme.palette.mode === "dark" ? "rgb(68, 76, 86)" : "rgb(232, 240, 254)";

  const outlineInputRootSx = {
    borderRadius: "12px",
    "& .MuiOutlinedInput-input": {
      py: 1.25,
    },
    "&:has(.MuiOutlinedInput-input:-webkit-autofill)": {
      backgroundColor: autofillBg,
    },
    "& .MuiOutlinedInput-input:-webkit-autofill": {
      WebkitBoxShadow: `0 0 0 1000px ${autofillBg} inset`,
      WebkitTextFillColor: theme.palette.text.primary,
      caretColor: theme.palette.text.primary,
      transition: "background-color 5000s ease-in-out 0s",
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        isolation: "isolate",
        minHeight: "100vh",
        overflowX: "hidden",
        px: 2,
        py: { xs: 3, md: 5 },
        boxSizing: "border-box",
        background: USER_AUTH_BACKGROUND,
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        "&::before": {
          content: '""',
          position: "fixed",
          inset: 0,
          zIndex: -1,
          pointerEvents: "none",
          opacity: 0.42,
          backgroundImage:
            "linear-gradient(rgba(71,91,99,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(71,91,99,0.035) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage:
            "linear-gradient(to bottom, rgba(0,0,0,0.7), transparent 88%)",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 500,
          minWidth: 0,
          mx: { xs: "8px", sm: "40px", md: "48px" },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <UserAuthCard>
          <Box
            component="fieldset"
            disabled={authBusy}
            sx={{
              border: 0,
              m: 0,
              minWidth: 0,
              px: 3,
              py: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              overflowY: "auto",
              overflowX: "hidden",
              bgcolor: "transparent",
              borderRadius: "20px",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <UserLoginNotice
              value={loginNotice({
                notice,
                busy: authBusy || isPairing || loadingForgotPassword,
                progressTitle: loadingForgotPassword
                  ? "Sending your reset code"
                  : authStage === "google"
                    ? "Opening Google sign-in"
                    : undefined,
                loading:
                  flow === "callback" && isValidRequest && !showLoginForm,
                pairing: flow === "pairing" && !showLoginForm,
                requester: clientDisplayName,
                challenge: challenge ? signInChallengeMessage(challenge) : null,
                reset: showResetFields,
                signingUp: activeTab === 1,
                delivery: deliveryPending,
                expiring: showExpirationWarning,
                remaining: timeRemaining,
              })}
            />

            {/* Pairing flow */}
            {isValidRequest && flow === "pairing" && !showLoginForm && (
              <Box sx={{ mt: 4 }}>
                <VerifyCode
                  value={pairingCode}
                  onChange={handlePairingCodeChange}
                  onComplete={handlePairingCodeComplete}
                  disabled={isPairing}
                />
              </Box>
            )}

            {isValidRequest && showLoginForm && challenge && (
              <UserSignInChallenge
                key={challenge.signInStep}
                step={challenge}
                busy={authBusy}
                onConfirm={handleChallenge}
                onCancel={backToSignIn}
                onResend={() =>
                  runAuth(async () => {
                    await resendSignUpCode({ username: email.trim() });
                    setInfoMessage(
                      "A new verification code has been sent to your email."
                    );
                  }, "verification")
                }
              />
            )}
            {isValidRequest && showLoginForm && deliveryPending && (
              <Stack spacing={1} sx={{ mt: 2 }}>
                <Button
                  disabled={authBusy}
                  variant="contained"
                  onClick={() => runAuth(finishSignIn, "delivery")}
                >
                  Retry connection
                </Button>
                <Button disabled={authBusy} onClick={backToSignIn}>
                  Sign in again
                </Button>
              </Stack>
            )}

            {/* Normal login/signup flow - only show if valid request and should show login form */}
            {showNormalLoginForm && (
              <Box sx={{ mt: 2 }}>
                {!showResetFields && (
                  <>
                    <TextField
                      hiddenLabel
                      placeholder="Email"
                      variant="outlined"
                      fullWidth
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={() => setEmailError(false)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          ...outlineInputRootSx,
                          ...(emailError && {
                            "& fieldset": { borderColor: "error.main" },
                            "&:hover fieldset": { borderColor: "error.main" },
                          }),
                        },
                      }}
                      error={emailError}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon
                              sx={{
                                width: 20,
                                height: 20,
                                color: "primary.main",
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {activeTab == 0 && (
                      <TextField
                        hiddenLabel
                        placeholder="Password"
                        variant="outlined"
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={() => setPasswordError(false)}
                        sx={{
                          mt: 2,
                          "& .MuiOutlinedInput-root": {
                            ...outlineInputRootSx,
                            ...(passwordError && {
                              "& fieldset": { borderColor: "error.main" },
                              "&:hover fieldset": { borderColor: "error.main" },
                            }),
                          },
                        }}
                        error={passwordError}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon
                                sx={{
                                  width: 20,
                                  height: 20,
                                  color: "primary.main",
                                }}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={togglePasswordVisibility}
                                onMouseDown={(e) => e.preventDefault()} // Prevent focus loss on mouse down
                              >
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  </>
                )}

                {activeTab == 1 && (
                  <>
                    <TextField
                      hiddenLabel
                      placeholder="Password"
                      variant="outlined"
                      type={showPassword ? "text" : "password"}
                      fullWidth
                      value={password}
                      onChange={(e) => handlePasswordChange(e)}
                      onFocus={() => setPasswordError(false)}
                      sx={{
                        mt: 2,
                        "& .MuiOutlinedInput-root": {
                          ...outlineInputRootSx,
                          ...(passwordError && {
                            "& fieldset": { borderColor: "error.main" },
                            "&:hover fieldset": { borderColor: "error.main" },
                          }),
                        },
                      }}
                      error={passwordError}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon
                              sx={{
                                width: 20,
                                height: 20,
                                color: "primary.main",
                              }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={togglePasswordVisibility}
                              onMouseDown={(e) => e.preventDefault()} // Prevent focus loss on mouse down
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {password && (
                      <Box
                        component="ul"
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, 1fr)",
                          columnGap: 2,
                          rowGap: 0.5,
                          mt: 0.5,
                          py: 0.5,
                          pl: 0,
                          m: 0,
                          fontSize: "0.75rem",
                          textAlign: "left",
                          listStyle: "none",
                          "& li": {
                            listStyle: "none",
                            lineHeight: 1.2,
                            mb: 0,
                          },
                        }}
                      >
                        <Box
                          component="li"
                          sx={{
                            color: validations.hasLowerCase
                              ? "success.main"
                              : "error.main",
                          }}
                        >
                          {validations.hasLowerCase ? "✓" : "×"} Lowercase
                          letter
                        </Box>
                        <Box
                          component="li"
                          sx={{
                            color: validations.hasUpperCase
                              ? "success.main"
                              : "error.main",
                          }}
                        >
                          {validations.hasUpperCase ? "✓" : "×"} Uppercase
                          letter
                        </Box>
                        <Box
                          component="li"
                          sx={{
                            color: validations.hasNumber
                              ? "success.main"
                              : "error.main",
                          }}
                        >
                          {validations.hasNumber ? "✓" : "×"} Number
                        </Box>
                        <Box
                          component="li"
                          sx={{
                            color: validations.hasMinLength
                              ? "success.main"
                              : "error.main",
                          }}
                        >
                          {validations.hasMinLength ? "✓" : "×"} Min 8 chars
                        </Box>
                        <Box
                          component="li"
                          sx={{
                            color: validations.hasSpecialChar
                              ? "success.main"
                              : "error.main",
                          }}
                        >
                          {validations.hasSpecialChar ? "✓" : "×"} Special char
                        </Box>
                        {!validations.hasSpecialChar && (
                          <Box
                            component="li"
                            sx={{
                              color: "error.main",
                              gridColumn: "1 / -1",
                              fontSize: "0.7rem",
                            }}
                          >
                            Accepted: !@#$%^&*(),.?&quot;:{}|&lt;&gt;
                          </Box>
                        )}
                        <Box
                          component="li"
                          sx={{
                            color: validations.noLeadingTrailingSpace
                              ? "success.main"
                              : "error.main",
                            gridColumn: "1 / -1",
                          }}
                        >
                          {validations.noLeadingTrailingSpace ? "✓" : "×"} No
                          leading/trailing space
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}

            {/* Forgot password button */}
            {showNormalLoginForm && (
              <Box sx={{ textAlign: "left" }}>
                {activeTab === 0 && !showResetFields && (
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={handleForgotPassword}
                    disabled={loadingForgotPassword}
                    sx={{
                      color: "primary.main",
                      textTransform: "none",
                      bgcolor: "transparent",
                      p: 0,
                      minHeight: 0,
                      height: "auto",
                      fontWeight: 400,
                      "&:hover": {
                        bgcolor: "transparent",
                        fontWeight: 700,
                      },
                    }}
                  >
                    Forgot Password?
                    {loadingForgotPassword && (
                      <CircularProgress
                        size={14}
                        sx={{ ml: 1, color: "primary.main" }}
                      />
                    )}
                  </Button>
                )}
              </Box>
            )}

            {/* Reset password fields */}
            {showNormalLoginForm && showResetFields && activeTab == 0 && (
              <>
                <TextField
                  hiddenLabel
                  placeholder="Reset Code"
                  fullWidth
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  sx={{
                    mt: 1,
                    "& .MuiOutlinedInput-root": { ...outlineInputRootSx },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKeyIcon
                          sx={{
                            width: 20,
                            height: 20,
                            color: "primary.main",
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  hiddenLabel
                  placeholder="New Password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  value={newPassword}
                  onChange={(e) => handleNewPasswordChange(e)}
                  sx={{
                    mt: 1.5,
                    "& .MuiOutlinedInput-root": { ...outlineInputRootSx },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon
                          sx={{
                            width: 20,
                            height: 20,
                            color: "primary.main",
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          onMouseDown={(e) => e.preventDefault()} // Prevent focus loss on mouse down
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {newPassword && (
                  <Box
                    component="ul"
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      columnGap: 2,
                      rowGap: 0.5,
                      mt: 0.5,
                      py: 0.5,
                      pl: 0,
                      m: 0,
                      fontSize: "0.75rem",
                      textAlign: "left",
                      listStyle: "none",
                      "& li": {
                        listStyle: "none",
                        lineHeight: 1.2,
                        mb: 0,
                      },
                    }}
                  >
                    <Box
                      component="li"
                      sx={{
                        color: validations.hasLowerCase
                          ? "success.main"
                          : "error.main",
                      }}
                    >
                      {validations.hasLowerCase ? "✓" : "×"} Lowercase letter
                    </Box>
                    <Box
                      component="li"
                      sx={{
                        color: validations.hasUpperCase
                          ? "success.main"
                          : "error.main",
                      }}
                    >
                      {validations.hasUpperCase ? "✓" : "×"} Uppercase letter
                    </Box>
                    <Box
                      component="li"
                      sx={{
                        color: validations.hasNumber
                          ? "success.main"
                          : "error.main",
                      }}
                    >
                      {validations.hasNumber ? "✓" : "×"} Number
                    </Box>
                    <Box
                      component="li"
                      sx={{
                        color: validations.hasMinLength
                          ? "success.main"
                          : "error.main",
                      }}
                    >
                      {validations.hasMinLength ? "✓" : "×"} Min 8 chars
                    </Box>
                    <Box
                      component="li"
                      sx={{
                        color: validations.hasSpecialChar
                          ? "success.main"
                          : "error.main",
                      }}
                    >
                      {validations.hasSpecialChar ? "✓" : "×"} Special char
                    </Box>
                    {!validations.hasSpecialChar && (
                      <Box
                        component="li"
                        sx={{
                          color: "error.main",
                          gridColumn: "1 / -1",
                          fontSize: "0.7rem",
                        }}
                      >
                        Accepted: !@#$%^&*(),.?&quot;:{}|&lt;&gt;
                      </Box>
                    )}
                    <Box
                      component="li"
                      sx={{
                        color: validations.noLeadingTrailingSpace
                          ? "success.main"
                          : "error.main",
                        gridColumn: "1 / -1",
                      }}
                    >
                      {validations.noLeadingTrailingSpace ? "✓" : "×"} No
                      leading/trailing space
                    </Box>
                  </Box>
                )}

                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  sx={{
                    mt: 2,
                    mb: 1,
                    borderRadius: "12px",
                    textTransform: "none",
                    boxShadow: "none",
                  }}
                  onClick={handleResetPassword}
                >
                  Reset Password
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 1, borderRadius: "12px", textTransform: "none" }}
                  onClick={() => setShowResetFields(false)}
                >
                  Cancel Reset Password
                </Button>
              </>
            )}

            {/* Terms and conditions checkbox - shown when login form is visible */}
            {showNormalLoginForm && !showResetFields && (
              <Box sx={{ mt: 1, mb: 1, ml: 0, pl: 0 }}>
                <FormControlLabel
                  sx={{
                    alignItems: "center",
                    ml: 0,
                    mr: 0,
                    width: "100%",
                    "& .MuiFormControlLabel-label": {
                      lineHeight: 1.4,
                    },
                  }}
                  control={
                    <Checkbox
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      sx={{
                        p: 0,
                        mr: 1.25,
                        pl: 0,
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                      I agree to the{" "}
                      <Box
                        component="span"
                        onClick={() =>
                          window.open("https://mhome.ai/terms-of-use", "_blank")
                        }
                        sx={{
                          color: "primary.main",
                          cursor: "pointer",
                          textDecoration: "underline",
                          "&:hover": { fontWeight: 700 },
                        }}
                      >
                        Terms of Use
                      </Box>{" "}
                      and{" "}
                      <Box
                        component="span"
                        onClick={() =>
                          window.open(
                            "https://mhome.ai/privacy-policy",
                            "_blank"
                          )
                        }
                        sx={{
                          color: "primary.main",
                          cursor: "pointer",
                          textDecoration: "underline",
                          "&:hover": { fontWeight: 700 },
                        }}
                      >
                        Privacy Policy
                      </Box>
                    </Typography>
                  }
                />
              </Box>
            )}

            {/* Main buttons section */}
            {showNormalLoginForm && !showResetFields && (
              <>
                <Box sx={{ textAlign: "center" }}>
                  {activeTab == 0 && (
                    <>
                      <Button
                        variant={termsAccepted ? "contained" : "outlined"}
                        onClick={() => {
                          handleSignIn();
                        }}
                        disabled={!termsAccepted}
                        fullWidth
                        sx={{
                          borderRadius: "12px",
                          mt: 2,
                          textTransform: "none",
                          boxShadow: "none",
                          ...(termsAccepted && {
                            bgcolor: "primary.main",
                            color: "white",
                            "&:hover": {
                              bgcolor: "primary.dark",
                            },
                          }),
                        }}
                      >
                        Sign In
                      </Button>
                    </>
                  )}
                  {activeTab == 1 && (
                    <Box>
                      <Button
                        variant="contained"
                        onClick={() => {
                          if (codeSent) {
                            handleResendCode();
                          } else {
                            handleSignUp();
                          }
                        }}
                        disabled={isCountdownActive || !termsAccepted}
                        fullWidth
                        sx={{
                          borderRadius: "12px",
                          mt: 3,
                          textTransform: "none",
                          boxShadow: "none",
                          bgcolor:
                            activeTab === 1 ? "primary.main" : "transparent",
                          color: activeTab === 1 ? "white" : "primary.main",
                          border: `1px solid ${theme.palette.primary.main}`,
                        }}
                      >
                        {isCountdownActive
                          ? `Resend in ${countdown}s`
                          : "Send Verification Code"}
                      </Button>
                    </Box>
                  )}
                </Box>

                {/* Google button - only show for sign-in (activeTab 0) */}
                {activeTab === 0 && (
                  <Box sx={{ textAlign: "center" }}>
                    <Button
                      variant="outlined"
                      startIcon={
                        <Box
                          component="img"
                          src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg"
                          sx={{ width: 20, height: 20 }}
                          alt="Google"
                        />
                      }
                      disabled={!termsAccepted}
                      fullWidth
                      sx={{
                        mt: 1.5,
                        borderRadius: "12px",
                        textTransform: "none",
                        boxShadow: "none",
                      }}
                      onClick={handleGoogleSignIn}
                    >
                      Sign in with Google
                    </Button>
                  </Box>
                )}

                {activeTab === 0 && (
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="body2"
                      sx={{ mt: 2, color: "text.primary" }}
                    >
                      Need an account?&nbsp;{" "}
                      <Box
                        component="span"
                        onClick={() => handleTabSwitch(1)}
                        sx={{
                          color: "primary.main",
                          cursor: "pointer",
                          "&:hover": { fontWeight: 700 },
                        }}
                      >
                        Sign Up
                      </Box>
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Signup confirmation section */}
            {showNormalLoginForm && activeTab === 1 && (
              <Box sx={{ textAlign: "center" }}>
                {showConfirm && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Button
                        variant="outlined"
                        sx={{
                          mr: 1,
                          borderRadius: "12px",
                          textTransform: "none",
                        }}
                      >
                        Verification Code :
                      </Button>
                      <TextField
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        sx={{
                          flex: 1,
                          mt: 2.5,
                          "& .MuiOutlinedInput-root": {
                            ...outlineInputRootSx,
                          },
                        }}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{
                        borderRadius: "12px",
                        mt: 2,
                        textTransform: "none",
                        color: "common.white",
                        boxShadow: "none",
                      }}
                      onClick={handleConfirmSignUp}
                    >
                      Confirm Sign Up
                    </Button>
                  </>
                )}
                <Typography
                  variant="body2"
                  sx={{ mt: 2, color: "text.primary" }}
                >
                  Already have an account? &nbsp;{" "}
                  <Box
                    component="span"
                    onClick={() => handleTabSwitch(0)}
                    sx={{
                      color: "primary.main",
                      cursor: "pointer",
                      "&:hover": { fontWeight: 700 },
                    }}
                  >
                    Sign In
                  </Box>
                </Typography>
              </Box>
            )}

            {/* Terms and conditions */}
            <Box
              sx={{
                mt: 3,
                textAlign: "center",
                fontSize: "0.875rem",
                color: "text.secondary",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                alignItems="center"
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  onClick={() =>
                    window.open("https://mhome.ai/terms-of-use", "_blank")
                  }
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                      fontWeight: 700,
                    },
                  }}
                >
                  Terms of Use
                </Typography>
                <Box component="span" sx={{ color: "text.secondary" }}>
                  •
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  onClick={() =>
                    window.open("https://mhome.ai/privacy-policy", "_blank")
                  }
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                      fontWeight: 700,
                    },
                  }}
                >
                  Privacy Policy
                </Typography>
              </Stack>
            </Box>
          </Box>
        </UserAuthCard>
      </Box>
    </Box>
  );
};

export default UserLogin;
